import React from 'react';
import { useTranslation } from 'react-i18next';

import type { CreativeJob } from '@/store/slices/creativeJobs';

import { Icon, SectionTitle, Text } from '../../../../components';
import { useThemeMode } from '../../../../theme/OwsTheme';
import { CUST_GEN_DOT_FILL, CUST_GEN_DOT_FILL_LIGHT } from '../../../../theme/customizer-tokens';
import { ART_STYLE } from '../../../../theme/tokens';
import { DotField } from '../../../customizer/components/DotField';
import { RunStatusLine } from '../../../creatives/components/StoryboardComposer/RunStatusLine';
import { ART_STYLE_SLOTS, type ArtStyle, type ArtStyleSample, type ArtStyleSlot } from '../../art-style-api';
import { samplesSinceExtraction, slotPhase, type SlotPhase } from '../../sample-jobs';
import type { ArtStyleBusy } from '../../useArtStyle';
import {
  Root,
  Head,
  HeadCopy,
  Lead,
  PreviewNote,
  Grid,
  Card,
  Media,
  Img,
  Placeholder,
  PlaceholderText,
  Overlay,
  Backdrop,
  OverlayCenter,
  OverlayText,
  RetryButton,
  Versions,
  VersionArrow,
  VersionCount,
  Foot,
  SlotName,
  SlotMeta,
} from './SamplesPanel.styles';

export type SamplesPanelProps = {
  style: ArtStyle;
  slotJobs: Partial<Record<ArtStyleSlot, CreativeJob>>;
  busy: ArtStyleBusy;
  /** Draw these slots (all four when omitted). The caller runs the affordability gate. */
  onDraw: (slots?: readonly ArtStyleSlot[]) => void;
  /** Per slot, a draw is on its way with no job of its own yet (an extraction runs, or it queued the samples and
   *  their jobs are not in the feed yet): the card shows as drawing until its job takes over. */
  starting?: Readonly<Partial<Record<ArtStyleSlot, boolean>>>;
};

/** Each slot's shape (CSS aspect-ratio) and its label beside the name. */
const SLOT_SHAPE: Record<ArtStyleSlot, { ratio: string; aspect: string }> = {
  hero: { ratio: '2 / 1', aspect: '2:1' },
  promo: { ratio: '1 / 1', aspect: '1:1' },
  story: { ratio: '9 / 16', aspect: '9:16' },
  reward: { ratio: '1 / 1', aspect: '1:1' },
};

type SampleCardProps = {
  slot: ArtStyleSlot;
  label: string;
  samples: readonly ArtStyleSample[];
  job: CreativeJob | undefined;
  phase: SlotPhase;
  canDraw: boolean;
  onRedraw: () => void;
  seed: number;
};

/**
 * One sample format across its lifecycle: never drawn, drawing (the Lumio dot field with the elapsed time),
 * failed (with a retry), or showing its samples with arrows through the versions (newest first). Keyed by its newest
 * sample by the caller, so a fresh sample resets it to the newest version and fades in.
 */
const SampleCard = ({
  slot,
  label,
  samples,
  job,
  phase,
  canDraw,
  onRedraw,
  seed,
}: SampleCardProps): React.ReactElement => {
  const { t } = useTranslation();
  const [index, setIndex] = React.useState(0);
  const [loaded, setLoaded] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);
  const dotFill = useThemeMode() === 'light' ? CUST_GEN_DOT_FILL_LIGHT : CUST_GEN_DOT_FILL;

  const current = samples[Math.min(index, samples.length - 1)];
  const shape = SLOT_SHAPE[slot];

  // A cached image can finish decoding before onLoad attaches.
  React.useEffect(() => {
    setLoaded(Boolean(imgRef.current?.complete));
  }, [current?.url]);

  const slotPhrases: Record<ArtStyleSlot, string[]> = {
    hero: [
      t('Setting the scene'),
      t('Painting it in your style'),
      t('Bringing in your character'),
      t('Adding the final light'),
    ],
    promo: [t('Composing the tile'), t('Painting it in your style'), t('Adding the final light')],
    story: [t('Framing the story'), t('Painting it in your style'), t('Bringing in your character')],
    reward: [t('Shaping the reward'), t('Polishing the details'), t('Adding the final light')],
  };
  const drawingPhrases = slotPhrases[slot];

  return (
    <Card $area={slot}>
      <Media $ratio={shape.ratio}>
        {current ? (
          <Img
            ref={imgRef}
            src={current.url}
            alt={label}
            $loaded={loaded}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        ) : phase !== 'generating' ? (
          <Placeholder>
            <Icon name="grid" size={18} />
            <PlaceholderText text="Not drawn yet" tag="span" />
          </Placeholder>
        ) : null}

        {phase === 'generating' ? (
          <Overlay>
            <Backdrop $solid={!current} />
            <DotField seed={seed} fill={dotFill} />
            <OverlayCenter>
              {/* What Lumio is doing, typed in and cycled (its own role="status"); each card has its own lines. */}
              <RunStatusLine stacked phrases={drawingPhrases} />
            </OverlayCenter>
          </Overlay>
        ) : null}

        {phase === 'failed' ? (
          <Overlay>
            <Backdrop $solid />
            <OverlayCenter role="alert">
              <Icon name="warning" size={18} />
              <OverlayText text="This one didn't draw. Your credits were refunded." tag="span" />
              <RetryButton
                type="button"
                disabled={!canDraw}
                onClick={() => {
                  if (canDraw) onRedraw();
                }}
              >
                <Text text="Try again" tag="span" />
              </RetryButton>
            </OverlayCenter>
          </Overlay>
        ) : null}

        {samples.length > 1 && phase !== 'generating' ? (
          <Versions>
            <VersionArrow
              type="button"
              aria-label={t('Older version')}
              disabled={index >= samples.length - 1}
              onClick={() => setIndex((i) => Math.min(samples.length - 1, i + 1))}
            >
              <Icon name="arrow-left" size={12} />
            </VersionArrow>
            <VersionCount>
              <Text raw text={`${samples.length - index}/${samples.length}`} tag="span" />
            </VersionCount>
            <VersionArrow
              type="button"
              aria-label={t('Newer version')}
              $flip
              disabled={index === 0}
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
            >
              <Icon name="arrow-left" size={12} />
            </VersionArrow>
          </Versions>
        ) : null}
      </Media>
      <Foot>
        <SlotName raw text={label} tag="span" />
        <SlotMeta raw text={shape.aspect} tag="span" />
      </Foot>
    </Card>
  );
};

/**
 * Samples: the four formats drawn in the style (hero banner, promotion tile, social story, reward item), so
 * the owner can judge it before using it. Shown once the style has a definition: an extraction draws all four (its
 * cost is the four samples'), and a card whose draw failed retries on its own (the caller gates it on the balance).
 * Each draw is a creative job, so its progress arrives with the shared jobs feed.
 */
export const SamplesPanel = ({
  style,
  slotJobs,
  busy,
  onDraw,
  starting = {},
}: SamplesPanelProps): React.ReactElement => {
  const { t } = useTranslation();
  const labels: Record<ArtStyleSlot, string> = {
    hero: t('Hero banner'),
    promo: t('Promotion tile'),
    story: t('Social story'),
    reward: t('Reward item'),
  };

  const hasDefinition = style.definition.trim() !== '';

  return (
    <Root>
      <Head>
        <HeadCopy>
          <SectionTitle title={t('Samples')} icon="grid" />
          <Lead text="Four formats drawn in your style, so you can judge it before you use it." tag="span" />
          <PreviewNote>
            <Icon name="info" size={13} />
            <Text text="Previews only: they never appear on your website." tag="span" />
          </PreviewNote>
        </HeadCopy>
      </Head>
      <Grid>
        {ART_STYLE_SLOTS.map((slot, i) => {
          const samples = samplesSinceExtraction(style.samples[slot], style.extraction?.at);
          const job = slotJobs[slot];
          const phase = starting[slot] ? 'generating' : slotPhase(job, samples);
          return (
            <SampleCard
              key={`${slot}-${samples[0]?.job_id ?? samples[0]?.url ?? 'none'}`}
              slot={slot}
              label={labels[slot]}
              samples={samples}
              job={job}
              phase={phase}
              canDraw={hasDefinition && busy === null && phase !== 'generating'}
              onRedraw={() => onDraw([slot])}
              seed={i + 1}
            />
          );
        })}
      </Grid>
    </Root>
  );
};

export default SamplesPanel;
